import { API_BASE_URL } from "@/config";
import Swal from 'sweetalert2';

/**
 * Decrypts a PDF file if it's password protected using PDF.js
 * @param {File} pdf - The PDF file to process
 * @returns {Promise<File>} - A promise that resolves to the PDF file (original or decrypted)
 */
export function pdf_decryptify(pdf) {
    if (typeof window.pdfjsLib == "undefined") {
        return new Promise((resolve, reject) => {
            load_pdf_js().then(() => {
                resolve(pdf_decryptify(pdf));
            }).catch((err) => {
                console.error('[pdf_decryptify] Failed to load PDF.js:', err);
                reject(null);
            });
        });
    }

    try {
        return new Promise((resolve, reject) => {
        const file_reader = new FileReader();

        file_reader.onload = async function() {
            const typed_array = new Uint8Array(this.result);

            try {
                // Step 1: Check if PDF is encrypted
                const loading_task = window.pdfjsLib.getDocument({ data: typed_array });
                const pdf_doc = await loading_task.promise;
                
                // If we can access the document without password, it's not encrypted
                try {
                    await pdf_doc.getPage(1);
                    // PDF is not encrypted, return original file
                    resolve(pdf);
                    return;
                } catch (readError) {
                    // If we can't read the page but loaded the document, it might still be encrypted
                    if (readError?.name !== 'PasswordException') {
                        throw readError;
                    }
                }
            } catch (error) {
                if (error?.name === 'PasswordException') {
                    // Step 2: PDF is encrypted, try ID number first
                    try {
                        const id_number_result = await check_pdf_id_number(pdf);
                        if (id_number_result) {
                            resolve(id_number_result);
                            return;
                        }
                    } catch (id_error) {
                        console.warn('[pdf_decryptify] ID number check failed:', id_error);
                    }
                    
                    // Step 3: ID number failed or not available, prompt for password
                    try {
                        const decrypted_file = await get_decrypted_pdf(pdf);
                        resolve(decrypted_file);
                    } catch (passwordError) {
                        if (passwordError.message === 'Password entry cancelled') {
                            resolve(passwordError);
                        } else {
                            reject(passwordError);
                        }
                    }
                } else {
                    console.error('[pdf_decryptify] Fatal error:', error);
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to open PDF: ' + error.message,
                        allowOutsideClick: false
                    });
                    reject(error);
                }
            }
        };

        file_reader.onerror = function() {
            console.error('[pdf_decryptify] FileReader error');
            reject(new Error('Failed to read file'));
        };

        file_reader.readAsArrayBuffer(pdf);
    });

    } catch (error) {
        console.error('[pdf_decryptify] Error:', error);
        return new Promise((resolve) => {
            resolve(null)
        });
    }
}

async function load_pdf_js() {
    return new Promise((resolve, reject) => {
        if (typeof window.pdfjsLib !== "undefined") {
            resolve();
            return;
        }
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js";
        script.onload = () => {
            resolve();
        };
        script.onerror = (err) => {
            console.error('[load_pdf_js] Failed to load script:', err);
            reject(err);
        };
        document.head.appendChild(script);
    });
}

function check_pdf_id_number(pdf_document) {
    return new Promise((resolve, reject) => {
        const BASE_URL = API_BASE_URL;
        const CHECK_PDF_ID_NUMBER_URL = `${BASE_URL}/verify_api/system_management_api/check_document_id_number/`;
        const TOKEN = localStorage.getItem('token');
        
        const form_data = new FormData();
        form_data.append('file', pdf_document);

        fetch(CHECK_PDF_ID_NUMBER_URL, {
            method: 'POST',
            body: form_data,
            headers: {
                'Authorization': `Token ${TOKEN}`
            }
        }).then(async (response) => {
            if (response.headers.get('Content-Type')?.includes('application/json')) {
                const data = await response.json();
                if (data.status === "success") {
                    // ID number check successful, return the file
                    resolve(null); // Will trigger password prompt
                } else {
                    reject(new Error(data.message || 'ID number check failed'));
                }
            } else {
                // Assume file response (Django FileResponse) - ID number worked
                const blob = await response.blob();
                const decrypted_file = new File([blob], pdf_document.name, { type: blob.type });
                resolve(decrypted_file);
            }
        }).catch((error) => {
            reject(error);
        });
    });
}

function prompt_pdf_password() {
    return new Promise((resolve, reject) => {
        const $container = $('<div>');
        
        const $label = $('<label>');
        $label.addClass('form-label text-xs mb-1').text('PDF Password');
        
        const $input_group = $('<div>');
        $input_group.addClass('input-group mb-2');
        
        const $input = $('<input>');
        $input.attr({
            id: 'password-input-field',
            type: 'password',
            class: 'form-control form-control-sm',
            placeholder: 'Enter password',
            autocomplete: 'off'
        });
        
        const $toggle_btn = $('<button>');
        $toggle_btn.addClass('btn btn-transparent btn-sm')
            .attr('type', 'button')
            .attr('tabindex', '-1');
        
        const $icon = $('<i>');
        $icon.addClass('bi bi-eye-slash').attr('aria-label', 'Show/Hide password');
        $toggle_btn.append($icon);
        
        const $error_text = $('<div>');
        $error_text.addClass('text-danger text-xs mt-1')
            .attr('id', 'password-input-error')
            .css('display', 'none');

        $input_group.append($input).append($toggle_btn);
        $container.append($label).append($input_group).append($error_text);

        Swal.fire({
            title: 'Enter PDF Password',
            html: $container,
            showCancelButton: true,
            confirmButtonText: 'Submit',
            cancelButtonText: 'Cancel',
            focusConfirm: true,
            allowOutsideClick: false,
            preConfirm: () => {
                const password = $('#password-input-field').val();
                if (!password) {
                    $('#password-input-error').text('Password is required.').show();
                    return false;
                }
                $('#password-input-error').hide();
                return password;
            },
            didOpen: () => {
                // Attach toggle event after DOM is rendered
                const $swal_content = $(Swal.getHtmlContainer());
                const $input = $swal_content.find('input');
                const $toggle_btn = $swal_content.find('button');
                $toggle_btn.on('click', function() {
                    const $icon = $(this).find('i');
                    if ($input.attr('type') === 'password') {
                        $input.attr('type', 'text');
                        $icon.removeClass('bi-eye-slash').addClass('bi-eye');
                    } else {
                        $input.attr('type', 'password');
                        $icon.removeClass('bi-eye').addClass('bi-eye-slash');
                    }
                });
                $input.trigger('focus');
                Swal.getConfirmButton().tabIndex = 0;
                $input.on('keydown', function(e) {
                    if (e.key === 'Enter') {
                        Swal.clickConfirm();
                    }
                });
            }
        }).then((result) => {
            if (result.isConfirmed) {
                var value = $('#password-input-field').val();
                resolve(value);
            } else {
                reject(new Error('Password entry cancelled'));
            }
        });
    });
}

function check_pdf_password_loop(pdf_document) {
    let password_correct = false;
    let cancelled = false;
    let validated_password = null;
    
    return new Promise((resolve, reject) => {
        (async function loop() {
            while (!password_correct && !cancelled) {
                try {
                    const password = await prompt_pdf_password();
                    if (!password) {
                        cancelled = true;
                        reject(new Error('Password entry cancelled'));
                        return;
                    }

                    // Validate password with PDF.js
                    const file_reader = new FileReader();
                    const pdf_validation = new Promise((resolve_validation, reject_validation) => {
                        file_reader.onload = async function() {
                            const typed_array = new Uint8Array(this.result);
                            try {
                                const loading_task = window.pdfjsLib.getDocument({
                                    data: typed_array,
                                    password: password
                                });
                                await loading_task.promise;
                                resolve_validation(true);
                            } catch (error) {
                                if (error?.name === 'PasswordException') {
                                    reject_validation(new Error('Incorrect password'));
                                } else {
                                    reject_validation(error);
                                }
                            }
                        };
                        file_reader.readAsArrayBuffer(pdf_document);
                    });

                    await pdf_validation;
                    password_correct = true;
                    validated_password = password;
                    resolve(validated_password);
                    return;
                } catch (error) {
                    if (error.message === 'Incorrect password') {
                        await Swal.fire({
                            icon: 'error',
                            title: 'Incorrect Password',
                            text: 'Please try again.',
                            allowOutsideClick: false
                        });
                    } else {
                        reject(error);
                        return;
                    }
                }
            }
        })();
    });
}

function get_decrypted_pdf(pdf_document) {
    return new Promise((resolve, reject) => {
        (async function run() {
            try {
                const password = await check_pdf_password_loop(pdf_document);
                if (!password) {
                    reject(new Error('Password entry cancelled'));
                    return;
                }

                const BASE_URL = API_BASE_URL;
                const DECRYPT_URL = `${BASE_URL}/verify_api/system_management_api/get_pdf_decrypted/`;
                const TOKEN = localStorage.getItem('token');

                const form_data = new FormData();
                form_data.append('file', pdf_document);
                form_data.append('password', password);

                fetch(DECRYPT_URL, {
                    method: 'POST',
                    body: form_data,
                    headers: {
                        'Authorization': `Token ${TOKEN}`
                    }
                })
                .then(async (response) => {
                    const content_type = response.headers.get('Content-Type');
                    if (content_type && content_type.includes('application/json')) {
                        const data = await response.json();
                        if (data.status !== "success") {
                            Swal.fire({
                                icon: 'error',
                                title: 'Error',
                                text: data.message || 'Failed to decrypt PDF.',
                                allowOutsideClick: false
                            });
                            reject(new Error(data.message || 'Failed to decrypt PDF.'));
                        } else {
                            resolve(data);
                        }
                    } else {
                        // Assume file response (Django FileResponse)
                        const decrypted_blob = await response.blob();
                        const decrypted_file = new File([decrypted_blob], pdf_document.name, { type: decrypted_blob.type });
                        resolve(decrypted_file);
                    }
                })
                .catch((error) => {
                    console.error('[get_decrypted_pdf] Fetch error:', error);
                    reject(error);
                });
            } catch (error) {
                console.error('[get_decrypted_pdf] Error:', error);
                reject(error);
            }
        })();
    });
}